import { Component, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { StatusCellRendererComponent } from './status-cell-renderer.component';

interface CareerYearBoxesParams extends ICellRendererParams {
	onBoxClick?: (career: any, year: number) => void;
}

@Component({
	selector: 'core-career-year-boxes-cell-renderer',
	template: `
		<div class="year-row">
			@for (box of boxes; track box) {
				<button type="button" class="year-box" [ngClass]="box.color" [title]="box.tooltip" (click)="onClick(box.year)">
					<fa-icon icon="calendar-alt"></fa-icon>
					<span class="num">{{ box.year }}</span>
				</button>
			}
		</div>
	`,
	styles: [
		`
			.year-row {
				display: inline-flex;
				flex-wrap: nowrap;
				align-items: center;
				gap: 2px;
				white-space: nowrap;
			}
			.year-box {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				gap: 1px;
				width: 30px;
				height: 22px;
				border-radius: 4px;
				border: 1px solid #cbd5e1;
				background: #fff;
				cursor: pointer;
				font-size: 9px;
				color: #475569;
				padding: 0 2px;
				flex: 0 0 auto;
			}
			.year-box.success {
				background: #d1fae5;
				border-color: #10b981;
				color: #065f46;
			}
			.year-box.info {
				background: #dbeafe;
				border-color: #3b82f6;
				color: #1e3a8a;
			}
			.year-box.warning {
				background: #fef3c7;
				border-color: #f59e0b;
				color: #78350f;
			}
			.year-box.orange {
				background: #fed7aa;
				border-color: #f97316;
				color: #7c2d12;
			}
			.year-box.secondary {
				background: #f1f5f9;
				border-color: #cbd5e1;
				color: #94a3b8;
			}
			.year-box:hover {
				transform: scale(1.05);
			}
			.year-box .num {
				font-weight: 700;
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CareerYearBoxesCellRendererComponent implements ICellRendererAngularComp {
	boxes: Array<{ year: number; color: string; tooltip: string }> = [];
	private params!: CareerYearBoxesParams;

	constructor(private _translateService: TranslateService) {}

	agInit(params: CareerYearBoxesParams): void {
		this.params = params;
		const career = params.data;
		const duration =
			Number(
				career?.didactic_program?.duration ??
					career?.career_years?.length ??
					career?.profile_user_university_school_university?.school_university?.school?.duration ??
					5
			) || 5;
		const currentYear = Number(career?.current_year ?? 1);
		const careerYears: any[] = career?.career_years || [];

		this.boxes = [];
		for (let y = 1; y <= duration; y++) {
			const cy = careerYears.find(item => Number(item.year) === y);
			let color: string = 'secondary';
			let tooltip = this._translateService.instant('shared_career_year_box_tooltip_no_registration', { year: y });

			if (cy) {
				const nonPrimary: any[] = (cy.evaluations ?? []).filter((e: any) => !e.primary);
				const hasEvals = nonPrimary.length > 0;
				const allEvalsConfirmed = hasEvals && nonPrimary.every((e: any) => e.confirmed);

				if (cy.exam_confirmed && allEvalsConfirmed) {
					// Verde: CFU completi + valutazioni inserite + data e voto esame
					color = 'success';
					tooltip = this._translateService.instant('shared_career_year_box_tooltip_evaluated', { year: y });
				} else if (cy.exam_confirmed) {
					// Giallo: CFU completi ma valutazioni incomplete
					color = 'warning';
					tooltip = this._translateService.instant('shared_career_year_box_tooltip_evaluations_incomplete', { year: y });
				} else {
					// Arancione: CFU incompleti
					color = 'orange';
					tooltip = this._translateService.instant('shared_career_year_box_tooltip_cfu_incomplete', { year: y });
				}
			}

			this.boxes.push({ year: y, color, tooltip });
		}
	}

	onClick(year: number): void {
		if (this.params?.onBoxClick && this.params?.data) {
			this.params.onBoxClick(this.params.data, year);
		}
	}

	refresh(params: ICellRendererParams): boolean {
		this.agInit(params as CareerYearBoxesParams);
		return true;
	}
}
